import React, { useEffect, useRef, useState, useCallback } from 'react'
import { Box, Button, IconButton, Skeleton, Slider, TextField, Tooltip, Typography, useTheme } from '@mui/material'
import CustomSelectField from '../../../../components/common/CustomSelectField'
import SearchList from '../../../../shared/components/common/SearchList.jsx'
import VisuallyHiddenInput from '../../../../components/common/VisuallyHiddenInput'
import { Icons, debounce, formatVideoDuration, getExtraSIfPlural, getFileSizeFromBytes } from '../../../../utils/utilities'
import CustomImage from '../../../../shared/components/common/CustomImage.jsx'
import api from '../../../../service/api.js'
import { toast } from 'react-toastify'
import CustomChip from '../../../../components/common/CustomChip.js'
import { useOrganizationStore } from '../../../../store/organization.store.js'

const THUMBNAIL_TICKS = [
    { frameAt: 22, frameAtType: 'second' },
    { frameAt: 23, frameAtType: 'second' },
    { frameAt: 24, frameAtType: 'second' },
    { frameAt: 25, frameAtType: 'second' },
    { frameAt: 30, frameAtType: 'second' },
]

// Clamps a raw seconds value into [0, duration] — guards against negative offsets or offsets past the end of the video
const clampSeconds = ( seconds, duration ) => {
    let clamped = seconds
    if ( isNaN( clamped ) || clamped < 0 ) clamped = 0
    if ( duration && clamped > duration ) clamped = duration
    return clamped
}

const resolveTickSeconds = ( { frameAt, frameAtType }, duration ) => {
    if ( frameAtType === 'second' ) return clampSeconds( frameAt, duration )
    const percent = Math.min( Math.max( frameAt, 0 ), 100 )
    return ( ( duration || 0 ) * percent ) / 100
}

// Rounded seconds double as the cache/selection key so ticks defined in seconds or percent share one keyspace
const tickCacheKey = ( seconds ) => Math.round( seconds )

const RecordingFileCard = ( { entry, onPatch, onRemove, modules, moduleNameMap, onModuleNameCommit } ) => {

    const { palette, border, layout } = useTheme()

    const OrganizationStore = useOrganizationStore()


    const [subjects, setSubjects] = useState( entry?.subjects )
    const [loadingSubjects, setLoadingSubjects] = useState( false )
    const [frameThumbUrl, setFrameThumbUrl] = useState( null )
    const [isVideoReady, setIsVideoReady] = useState( false )
    const [thumbsReady, setThumbsReady] = useState( false )
    const [sliderValue, setSliderValue] = useState( 0 )
    const [tickThumbs, setTickThumbs] = useState( [] )

    const videoRef = useRef( null )
    const canvasRef = useRef( null )


    // Core Frame Capture Helper with dedicated event handling
    const captureFrameAtSeconds = useCallback( ( seconds ) => {
        return new Promise( ( resolve, reject ) => {
            const video = videoRef.current
            let targetTime = seconds
            if ( isNaN( targetTime ) || targetTime < 0.05 ) targetTime = 0.05
            if ( targetTime > video.duration - 0.05 ) targetTime = video.duration - 0.05

            video.requestVideoFrameCallback( ( now, meta ) => {

                try {
                    const canvas = canvasRef.current || document.createElement( 'canvas' )
                    canvas.width = video.videoWidth || 320
                    canvas.height = video.videoHeight || 180
                    const ctx = canvas.getContext( '2d' )
                    ctx.drawImage( video, 0, 0, canvas.width, canvas.height )

                    canvas.toBlob( ( blob ) => {
                        if ( blob ) {
                            resolve( { url: URL.createObjectURL( blob ), blob } )
                        } else {
                            resolve( null )
                        }
                    }, 'image/jpeg', 0.8 )
                } catch ( err ) {
                    console.log( err )
                    reject( err )
                }
            } )

            video.currentTime = targetTime
        } )

    }, [] )

    const handleMetadataLoad = async ( event ) => {
        try {
            const video = videoRef.current
            if ( video.duration && !entry.durationSeconds ) {
                const videoMetadata = {
                    videoHeight: video.videoHeight,
                    videoWidth: video.videoWidth,
                    duration: video.duration,
                    durationMs: video.duration * 1000,
                }
                let orientation = 'square'
                if ( video.videoWidth > video.videoHeight ) {
                    orientation = 'landscape'
                } else if ( video.videoHeight > video.videoWidth ) {
                    orientation = 'portrait'
                }
                videoMetadata.videoOrientation = orientation
                videoMetadata.original_name = entry.file.name
                videoMetadata.size = entry.file.size
                onPatch( entry.id, { recording_metadata: videoMetadata } )
            }
            setIsVideoReady( true )

            setSliderValue( Math.min( ( 25 * 100 ) / video.duration, 100 ) )

            const thumbsMap = {}
            for ( const tick of THUMBNAIL_TICKS ) {
                const seconds = resolveTickSeconds( tick, video.duration )
                const key = tickCacheKey( seconds )
                if ( !tickThumbs[key] ) {
                    const tickImg = await captureFrameAtSeconds( seconds )
                    thumbsMap[key] = tickImg
                }
            }
            setTickThumbs( prev => ( { ...prev, ...thumbsMap } ) )
            setThumbsReady( true )
            onPatch( entry.id, {
                frame: tickCacheKey( resolveTickSeconds( THUMBNAIL_TICKS[0], video.duration ) ),
                frameThumbBlobs: {
                    ...entry.frameThumbBlobs,
                    ...Object.fromEntries( Object.entries( thumbsMap ).filter( ( [, v] ) => v ).map( ( [k, v] ) => [k, v.blob] ) )
                }
            } )
        } catch ( err ) {
            console.log( err )

        }
    }

    const handleInstituteChange = ( event ) => {
        const institute = OrganizationStore.getOrganization?.find( i => i.institute_name_short === event.target.value )
        onPatch( entry.id, {
            institute
        } )
    }

    const handleSubjectChange = ( event ) => {
        const selectedSubjects = subjects?.filter( s => event.target.value?.includes( s.subject_code ) )
        onPatch( entry.id, {
            selectedSubjects
        } )
    }

    // Auto-fills the module name from the shared subject/module -> module_name map (seeded from the repository
    // page and kept live by every card's handleModuleNameBlur below), since a module's name can differ per
    // subject and isn't otherwise known from the picked file.
    const handleModuleNumberChange = ( event ) => {
        const module_number = event.target.value
        const subjectCodes = ( entry.subjects || [] ).map( s => s.subject_code )
        const matchedName = subjectCodes.map( code => moduleNameMap?.[code]?.[module_number] ).find( name => name )
        onPatch( entry.id, { module_number, ...( matchedName ? { module_name: matchedName } : { module_name: "" } ) } )
    }

    const handleModuleNameChange = event => {
        onPatch( entry.id, { module_name: event.target.value } )
    }

    // Shares a finished module name with the rest of the batch, so other selected videos on the same module
    // (picked before or after this one) pick it up instead of being stuck with whatever the backend map had.
    const handleModuleNameBlur = event => {
        const module_name = event.target.value?.trim()
        if ( !module_name ) return
        const subjectCodes = ( entry.subjects || [] ).map( s => s.subject_code )
        onModuleNameCommit?.( subjectCodes, entry.module_number, module_name )
    }

    const handleVideoFrameReady = async event => {
    }

    const handleSeeked = event => {
    }

    const handleCurrentTimeUpdate = event => {
    }

    const handleProgress = event => {

    }
    const debouncedSubjectSearch = useCallback( debounce( `video-subject-search`, async filter => {
        setLoadingSubjects( true )
        let fetchedSubjects = []
        if ( filter && filter.trim().length > 2 ) {
            try {
                const { data: { data } } = await api.audit.fetchSubjects( { subject_code: filter, institute: entry.institute?.institute_name_short } )
                fetchedSubjects = data
            } catch ( err ) {
                toast( err?.response?.data?.message || `Failed to fetch subjects for '${filter}'.` )
            }
        }

        const availableSubjects = ( entry?.subjects || [] ).map( sub => sub.subject_code )
        if ( entry?.subjects && !fetchedSubjects.find( sub => availableSubjects.includes( sub.subject_code ) ) )
            fetchedSubjects.push( ...entry.subjects )

        setSubjects( fetchedSubjects )
        setLoadingSubjects( false )
    }, 500 ), [entry.institute] )

    const setCustomFrameAsThumbnail = debounce( entry.id, async ( percentValue ) => {
        const seconds = resolveTickSeconds( { frameAt: percentValue, frameAtType: 'percent' }, entry.durationSeconds )
        const key = tickCacheKey( seconds )
        let captured = tickThumbs[key]
        if ( !captured ) {
            captured = await captureFrameAtSeconds( seconds )
            setTickThumbs( prev => ( { ...prev, [key]: captured } ) )
        }

        onPatch( entry.id, {
            frame: key,
            custom: false,
            frameThumbBlobs: captured ? { ...entry.frameThumbBlobs, [key]: captured.blob } : entry.frameThumbBlobs
        } )
    }, 500 )

    const handleSliderValueChange = ( _, value ) => {
        setSliderValue( value )
        setCustomFrameAsThumbnail( value )
    }

    // // Capture main preview thumbnail whenever entry.frame changes
    useEffect( () => {
        setFrameThumbUrl( tickThumbs[entry.frame]?.url )
    }, [entry.frame, tickThumbs] )

    const frameTime = entry.custom
        ? "Custom"
        : entry.durationSeconds
            ? formatVideoDuration( entry.frame || 0 )
            : "--:--"

    const activeThumbUrl = entry.custom ? entry.customThumbUrl : frameThumbUrl

    return (
        <Box border={border[1]} borderRadius={layout.borderRadius} padding="14px 16px">
            <canvas ref={canvasRef} style={{ display: 'none' }} />


            <Box display="flex" gap="12px" alignItems="center" marginBottom="14px">
                <Box width="56px" height="36px" flexShrink={0} borderRadius="6px" bgcolor={palette.contentBg} display="flex" alignItems="center" justifyContent="center">
                    {Icons.default.Movie}
                </Box>
                <Box flexGrow={1} minWidth={0}>
                    <Typography fontSize="12.5px" fontWeight="600" noWrap>{entry.file.name}</Typography>
                    <Typography fontSize="11.5px" color="textSecondary">{getFileSizeFromBytes( entry.file.size )} · {entry.durationSeconds ? formatVideoDuration( entry.durationSeconds ) : "Reading duration..."}</Typography>
                </Box>
                <IconButton size="small" onClick={() => onRemove( entry.id )}>{Icons.small.CloseIcon}</IconButton>
            </Box>

            <Box display="grid" gridTemplateColumns="repeat(auto-fit, minmax(200px, 1fr))" gap="12px">
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography sx={{ fontSize: "0.875rem" }} component="label" htmlFor={`${entry.id}-title`}  >Video title *</Typography>
                    <TextField sx={{ flexGrow: 1 }} size="small" id={`${entry.id}-title`} fullWidth value={entry.name} onChange={e => onPatch( entry.id, { name: e.target.value } )} placeholder="Eg: Linked lists — part 1" />
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography sx={{ fontSize: "0.875rem" }} component="label" htmlFor={`${entry.id}-institute`}  >Institute *</Typography>
                    <CustomSelectField
                        id={`${entry.id}-institute`}
                        renderOptionText={inst => <Box>
                            <Typography sx={{ fontSize: "14px", textTransform: "capitalize" }} >{inst.institute_name?.toLowerCase()}</Typography>
                            <CustomChip value={`${inst.institute_name_short}`} />
                        </Box>}
                        renderValue={instituteCode => {
                            const inst = OrganizationStore.getOrganization?.find( i => i.institute_name_short === instituteCode )
                            if ( !inst ) return instituteCode || "Select institute"
                            return (
                                <CustomChip value={inst.institute_name_short} />
                            )
                        }}
                        itemValueKey="institute_name_short"
                        onChange={handleInstituteChange}
                        value={entry?.institute?.institute_name_short || ""}
                        data={OrganizationStore.getOrganization}
                        size="small"
                        fullWidth
                        sx={{
                            flexGrow: 1,
                            overflow: "hidden"
                        }}
                    />
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography sx={{ fontSize: "0.875rem" }} component="label" htmlFor={`${entry.id}-subject`}  >Subject *</Typography>
                    <CustomSelectField
                        id={`${entry.id}-subject`}
                        disabled={!entry?.institute}
                        renderOptionText={sub => <Box>
                            <Typography sx={{ fontSize: "14px", textTransform: "capitalize" }} >{sub.subject_name?.toLowerCase()}</Typography>
                            <CustomChip value={`${sub.subject_code}`} />
                        </Box>}
                        searchPlaceholder="Search by subject code..."
                        loadinOptionsMessage="Searching subjects..."
                        onSearch={debouncedSubjectSearch}
                        renderValue={( subjectCodes = [] ) => {
                            if ( subjectCodes.length === 0 ) return "Select subject"

                            const moreSubjectsList = subjectCodes.slice( 1 ).map( ( subjectCode, idx ) => {
                                const subject = subjects.find( s => s.subject_code === subjectCode )
                                if ( !subject )
                                    return <Typography key={subjectCode} component="span" sx={{ display: "block", fontSize: "0.75rem", fontWeight: 500 }} >{`${idx + 2}. ${subjectCode}`}</Typography>
                                return <Typography key={subjectCode} component="span" sx={{ display: "block", fontSize: "0.75rem", fontWeight: 500 }} >{`${idx + 2}. ${subject.subject_name}`}</Typography>

                            } )

                            return <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                                {subjectCodes.slice( 0, 1 ).map( subjectCode => {
                                    const subject = subjects.find( s => s.subject_code === subjectCode )
                                    if ( !subject ) return subjectCode || "Select subject"
                                    return (
                                        <CustomChip
                                            key={subjectCode}
                                            value={subject.subject_code}
                                            tooltipTitle={subject.subject_name}
                                            noWrap
                                            textStyles={{ fontSize: "0.75rem" }}
                                        />
                                    )
                                } )}
                                {subjectCodes.length > 1 && <CustomChip
                                    tooltipTitle={<Box>
                                        {moreSubjectsList.map( s => s )}
                                    </Box>}
                                    value={`+${subjectCodes.length - 1} More`}
                                    noWrap
                                    textStyles={{ fontSize: "0.75rem" }}
                                />}

                            </Box>
                        }}
                        loadingOptions={loadingSubjects}
                        itemValueKey="subject_code"
                        onChange={handleSubjectChange}
                        value={( entry?.subjects || [] ).map( sub => sub.subject_code )}
                        minSearchLength={3}
                        data={subjects}
                        multiple
                        size="small"
                        fullWidth
                        sx={{
                            flexGrow: 1,
                            overflow: "hidden"
                        }}
                    />
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography sx={{ fontSize: "0.875rem" }} component="label" htmlFor={`${entry.id}-module_number`}  >Module *</Typography>
                    <CustomSelectField sx={{ fontWeight: 300 }} id={`${entry.id}-module_number`} size="small" value={entry.module_number} onChange={handleModuleNumberChange} data={modules} placeholder="Module" />
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography sx={{ fontSize: "0.875rem" }} component="label" htmlFor={`${entry.id}-module_name`}  >Module name</Typography>
                    <TextField sx={{ fontWeight: 300 }} id={`${entry.id}-module_name`} size="small" fullWidth value={entry.module_name ?? ""} onChange={handleModuleNameChange} onBlur={handleModuleNameBlur} placeholder="Eg: Linked Lists" />
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography sx={{ fontSize: "0.875rem" }} component="label" htmlFor={`${entry.id}-topics`}  >Topics covered in the video *</Typography>
                    <TextField sx={{ fontWeight: 300 }} id={`${entry.id}-topics`} size="small" fullWidth value={entry.topics_covered ?? ""} onChange={e => onPatch( entry.id, { topics_covered: e.target.value } )} placeholder="Eg: Singly linked list, Doubly Linked List" />
                    <Typography variant="body2" component="em" sx={{ fontSize: "12px", color: "text.secondary" }} >Enter as comma separated values.</Typography>
                </Box>
            </Box>

            <Box display="flex" gap="14px" alignItems="flex-start" marginTop="14px" paddingTop="14px" borderTop={`1px dashed ${palette.borderColor}`}>
                <Box width="132px" flexShrink={0}>
                    <video onProgress={handleProgress} onLoadedMetadata={handleMetadataLoad} hidden onTimeUpdate={handleCurrentTimeUpdate} onSeeked={handleSeeked} onCanPlay={handleVideoFrameReady} playsInline preload="auto" muted ref={videoRef} src={entry.localUrl} width="1024px" />
                    {thumbsReady ? <Box position="relative" width="132px" height="76px" borderRadius="6px" bgcolor={palette.common.font} display="flex" alignItems="center" justifyContent="center" overflow="hidden">
                        {activeThumbUrl ? (
                            <CustomImage src={activeThumbUrl} alt="Thumbnail preview" width="100%" height="100%" fit='cover' />
                        ) : (
                            <Box sx={{ opacity: .55, color: palette.common.bg, display: "flex" }}>{Icons.default.PlayArrowIcon}</Box>
                        )}
                        <Typography position="absolute" right="5px" bottom="5px" padding="1px 6px" borderRadius="4px" bgcolor="rgba(10,10,20,.7)" color="#fff" fontSize="10px">{frameTime}</Typography>
                    </Box> : <Skeleton width={132} height={76} />}
                </Box>

                <Box flexGrow={1} minWidth={0}>
                    <Box display="flex" alignItems="center" gap="8px" flexWrap="wrap">
                        <Typography fontSize="12px" fontWeight="600" color="textSecondary">Thumbnail</Typography>
                        <Typography fontSize="11.5px" color="textSecondary">{entry.custom ? "Custom image uploaded" : "Drag to pick the frame students will see"}</Typography>
                    </Box>

                    <Slider
                        step={0.01}
                        size="small"
                        color="secondary"
                        value={sliderValue ?? 0}
                        onChange={handleSliderValueChange}
                        disabled={entry.custom || !entry.durationSeconds || !isVideoReady}
                        sx={{ marginTop: "10px", display: "block" }}
                    />

                    <Box display="flex" gap="10px" marginTop="8px">
                        {THUMBNAIL_TICKS.map( ( tick, i ) => {
                            const seconds = resolveTickSeconds( tick, entry.durationSeconds )
                            const key = tickCacheKey( seconds )
                            const active = !entry.custom && entry.frame === key
                            const tickBg = tickThumbs[key]?.url

                            if ( !tickBg )
                                return <Skeleton key={i} sx={{ width: 100, aspectRatio: "16 / 9" }} />

                            return (
                                <Box
                                    key={key}
                                    component="button"
                                    type="button"
                                    onClick={() => ( setSliderValue( entry.durationSeconds ? ( seconds / entry.durationSeconds ) * 100 : 0 ), onPatch( entry.id, { frame: key, custom: false } ) )}
                                    sx={{
                                        aspectRatio: "16 / 9", width: 100,
                                        padding: 0, borderRadius: "4px", cursor: "pointer",
                                        outline: `2px solid ${active ? palette.secondary.main : palette.text.secondary}`,
                                        background: `url(${tickBg}) center/cover no-repeat`,
                                        opacity: entry.custom ? .35 : ( active ? 1 : .55 ),
                                        ":focus-visible": {
                                            outlineOffset: "2px",
                                            outline: `2px solid ${active ? palette.secondary.main : palette.text.primary}`,
                                        }
                                    }}
                                />
                            )
                        } )}
                    </Box>

                    <Box display="flex" flexWrap="wrap" gap="8px" marginTop="11px">
                        <Button component="label" size="small" variant="outlined" color="greyed" startIcon={Icons.default.UploadFileIcon} sx={{ textTransform: "capitalize", borderRadius: "18px" }}>
                            Upload thumbnail
                            <VisuallyHiddenInput type="file" accept="image/*" onChange={e => {
                                const file = e.target.files[0]
                                if ( file ) onPatch( entry.id, { custom: true, customThumbUrl: URL.createObjectURL( file ), customThumbFile: file } )
                                e.target.value = ""
                            }} />
                        </Button>
                        {entry.custom && <Button size="small" color="secondary" onClick={() => onPatch( entry.id, { custom: false } )} sx={{ textTransform: "capitalize" }}>Use a frame instead</Button>}
                    </Box>
                </Box>
            </Box>
        </Box>
    )
}

export default RecordingFileCard